import moment from 'moment-timezone';
import PreferenceTypes from '@/store/preference-types';

export class TimezoneHandler {
    store = null;

    /**
     * Attach a store to the current timezone handler instance for
     * future use when fetching the user's preferred timezone.
     * @param {Object} store
     */
    attach(store) {
        this.store = store;
    }

    /**
     * Returns the timezone string for the current user.
     * @returns {string}
     */
    tz() {
        return this.store.getters['user/getUserPreference'](PreferenceTypes.timezone);
    }

    /**
     * Create a moment instance for the current timezone
     * set to the current time.
     * @returns {*}
     */
    now() {
        return moment()
            .tz(this.tz());
    }

    /**
     * Create a moment instance in the user's timezone
     * from an UTC value.
     * @param {string} date
     */
    readUTC(date) {
        return moment.utc(date)
            .tz(this.tz());
    }

    /**
     * Create a moment instance in the user's timezone
     * using a specific format.
     * @param date
     * @param format
     * @returns {*}
     */
    readUTCFormat(date, format) {
        return moment.utc(date, format)
            .tz(this.tz());
    }

    /**
     * Convert UTC time into a moment.
     * @param time
     * @returns {*}
     */
    readUTCTime(time) {
        return moment.utc(time, 'HH:mm:ss')
            .tz(this.tz());
    }

    /**
     * Convert time from it's canonical time zone
     * to the user's time zone and return a moment.
     * @param time
     * @returns {*}
     */
    readTime(time) {
        return moment(time, 'HH:mm:ssZ')
            .tz(this.tz());
    }

    /**
     * Read a date without time and keep the local
     * time zone without skewing the hours.
     * e.g. 2018-10-10
     * @param {string} date
     * @returns {*}
     */
    readLocalDate(date) {
        return moment(date, 'YYYY-MM-DD')
            .tz(this.tz(), true);
    }

    /**
     * Get a raw API string from a timezone date
     * converted into UTC.
     * @param {string} date
     * @returns {string}
     */
    getRaw(date) {
        return moment.tz(date, this.tz())
            .utc()
            .format();
    }

    /**
     * Get a Moment instance from the date string in the user's preferred time zone.
     * @param {string|Moment} date
     * @returns {*}
     */
    getMoment(date) {
        return moment.tz(date, this.tz());
    }

    /**
     * Get a moment UTC instance without the time zone.
     * @return {*}
     */
    // eslint-disable-next-line
    getUTC() {
        return moment.utc();
    }

    /**
     * Get the integer offset representing the user's timezone.
     * @returns {int}
     */
    offset() {
        return this.now()
            .utcOffset();
    }

    /**
     * Convert a date in the user's current browser time zone,
     * to a moment instance in the user's profile time zone, all while
     * keeping the same time in both values.
     * @example   2016-01-15T11:58:07-08:00 >> 2016-01-15T11:58:07-05:00
     * @param date {string|Moment}
     * @returns {moment}
     */
    fromDate(date) {
        return moment(date)
            .tz(this.tz(), true);
    }
}

export default new TimezoneHandler();
